import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import HalfCourtSVG from '@/components/CourtCanvas';
import { api } from '@/lib/api';
import { useLang } from '@/context/LangContext';
import { useAuth } from '@/context/AuthContext';
import { TID } from '@/constants/testIds';
import { Search, Filter, Layers } from 'lucide-react';
import { displayPlayName } from '@/lib/playName';
import { putPlays, getAllPlays } from '@/lib/offlineDb';

const CATS = [
  { id: 'all', tk: 'all_categories' },
  { id: 'man', tk: 'cat_man' },
  { id: 'zone', tk: 'cat_zone' },
  { id: 'ato', tk: 'cat_ato' },
  { id: 'blob', tk: 'cat_blob' },
  { id: 'slob', tk: 'cat_slob' },
  { id: 'endgame', tk: 'cat_endgame' },
];

function MiniCourt({ play }) {
  const step = play?.steps?.[Math.min(1, (play?.steps?.length || 1) - 1)] || play?.steps?.[0];
  const V = 1000;
  return (
    <div className="relative w-full aspect-square rounded-md overflow-hidden border border-gold/30 bg-navy-deep">
      <HalfCourtSVG className="w-full h-full">
        {(step?.players || []).slice(0, 10).map(p => (
          <circle
            key={p.id}
            cx={(p.position.x / 100) * V}
            cy={(p.position.y / 100) * V}
            r="32"
            fill="#0A192F"
            stroke={p.team === 'offense' ? '#D4AF37' : '#E11D48'}
            strokeWidth="3"
          />
        ))}
        {(step?.players || []).slice(0, 10).map(p => (
          <text
            key={`t-${p.id}`}
            x={(p.position.x / 100) * V}
            y={(p.position.y / 100) * V + 9}
            textAnchor="middle"
            fontFamily="Oswald"
            fontWeight="700"
            fontSize="28"
            fill={p.team === 'offense' ? '#D4AF37' : '#E11D48'}
          >
            {p.label}
          </text>
        ))}
      </HalfCourtSVG>
    </div>
  );
}

export default function Playbook() {
  const { t } = useLang();
  const { user } = useAuth();
  const isPlayer = user?.role === 'player';
  const [plays, setPlays] = useState([]);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  // Counter sourced from the public Plays Hub — feeds the "PLAYBOOK 132+ JUGADAS"
  // banner. Polled at mount only; the value only changes on the scheduler tick.
  const [hubCounter, setHubCounter] = useState(null);

  useEffect(() => {
    api.get('/playshub/counter').then((r) => setHubCounter(r.data?.total ?? null)).catch(() => {});
  }, []);

  // We fetch ALL plays (filtered only by search) and apply the category
  // filter client-side. This keeps the category counts accurate at all times
  // ("ATO (3)") and lets the user toggle categories without re-fetching.
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    api.get('/plays', { params })
      .then(r => {
        const list = r.data.plays || [];
        setPlays(list);
        // Mirror to IndexedDB for offline access.
        putPlays(list).catch(() => {});
      })
      .catch(async () => {
        // Offline / network down → fall back to cached plays.
        const cached = await getAllPlays().catch(() => []);
        const filtered = cached.filter(p => {
          if (search && !(`${p.name} ${p.description || ''}`).toLowerCase().includes(search.toLowerCase())) return false;
          return true;
        });
        setPlays(filtered);
      })
      .finally(() => setLoading(false));
  }, [search]);

  // Apply category filter client-side over the already-loaded plays so the
  // counter chips reflect the real distribution.
  const visiblePlays = useMemo(
    () => category === 'all' ? plays : plays.filter(p => p.category === category),
    [plays, category],
  );

  return (
    <Layout>
      <BackToDashboard />
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('nav_playbook')}</div>
          <h1 className="font-h uppercase text-4xl sm:text-5xl" data-testid="playbook-counter">
            {hubCounter != null ? `${hubCounter}+ ` : '100+ '}{t('plays')}
          </h1>
        </div>
        {!isPlayer && (
          <Link
            to="/editor"
            className="px-4 py-2 rounded-md bg-gold text-navy font-h uppercase tracking-wider text-sm hover:bg-gold-light transition"
          >
            + {t('create_play')}
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6 bg-card-steel border border-gold/20 rounded-lg p-4 flex flex-col gap-3">
        <div className="flex-1 relative">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gold" />
          <input
            data-testid={TID.playSearch}
            placeholder={t('search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-navy-deep border border-steel focus:border-gold rounded-md ps-10 pe-4 py-2.5 text-white outline-none transition"
          />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="playbook-category-chips">
          {CATS.map(c => {
            const count = c.id === 'all' ? plays.length : plays.filter(p => p.category === c.id).length;
            const isActive = category === c.id;
            const isEmpty = count === 0 && c.id !== 'all';
            return (
              <button
                key={c.id}
                data-testid={`${TID.playFilterCategory}-${c.id}`}
                onClick={() => setCategory(c.id)}
                disabled={isEmpty}
                className={`px-3.5 py-2 rounded-full text-xs font-h uppercase tracking-wider transition border inline-flex items-center gap-1.5 ${
                  isActive
                    ? 'bg-gold text-navy border-gold shadow-lg'
                    : isEmpty
                    ? 'border-steel/40 text-gray-600 cursor-not-allowed'
                    : 'border-steel text-gray-300 hover:border-gold/60 hover:text-gold'
                }`}
              >
                {t(c.tk)}
                <span
                  className={`inline-flex items-center justify-center min-w-[20px] px-1 rounded-full text-[10px] font-mono ${
                    isActive ? 'bg-navy/30 text-navy' : 'bg-navy-deep/60 text-gold/70'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {loading && <div className="text-gray-400">{t('loading')}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5">
        {visiblePlays.map(p => (
          <Link
            key={p.id}
            to={`/playbook/${p.id}`}
            data-testid={`${TID.playCard}-${p.id}`}
            className="bg-card-steel border-2 border-gold/40 rounded-lg overflow-hidden hover-lift group"
          >
            <MiniCourt play={p} />
            <div className="p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-xs font-mono uppercase tracking-widest text-gold">
                  {p.category} · {p.difficulty}
                </div>
                <div className="text-xs font-mono text-gold border border-gold/40 rounded px-2 py-0.5">
                  {Math.round(p.success_rate * 100)}%
                </div>
              </div>
              <div className="font-h uppercase text-lg text-white group-hover:text-gold transition leading-tight">
                {(() => {
                  const { displayName, purchased } = displayPlayName(p);
                  return <>{displayName}{purchased && <span className="text-[10px] text-gold ms-1">{t('purchased_badge')}</span>}</>;
                })()}
              </div>
              <div className="text-xs text-gray-400 mt-1">{p.subcategory}</div>
            </div>
          </Link>
        ))}
      </div>

      {!loading && !visiblePlays.length && (
        <div className="text-center text-gray-400 py-12">
          <Layers className="w-10 h-10 mx-auto mb-2 text-gold/40" />
          {t('not_found')}
        </div>
      )}
    </Layout>
  );
}
